import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage } from '@/lib/api';
import { useEffect, useState } from 'react';
import { getWebApp, haptic, confirmAction } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { CheckCircle2, XCircle, Loader2, RotateCw, MessageCircleQuestion, Globe, Plus, Send, Handshake, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { AdminWebhookEvent, FxRate, PlanPriceInfo, PlatformBroadcast } from '@/api/types';
import { BottomSheet } from '@/components/ui/dialog';
import { APP_VERSION } from '@/lib/version';
import {
  MessagePreview,
  TemplateVarsHelper,
  useInsertAtCursor,
  type TemplateVar,
} from '@/components/BroadcastComposerHelpers';
import {
  SpeedSlider,
  ButtonsBuilder,
  MediaUploader,
  TestSendButton,
  BroadcastLiveView,
  type InlineButtonRows,
  type MediaValue,
} from '@/components/BroadcastControls';

type Category = 'events' | 'platform';
type SubTab = 'webhooks' | 'audit' | 'broadcasts' | 'trash' | 'pricing' | 'fx' | 'system' | 'perf' | 'settings';

const TABS_BY_CATEGORY: Record<Category, SubTab[]> = {
  events: ['webhooks', 'audit', 'broadcasts', 'trash'],
  platform: ['pricing', 'fx', 'system', 'perf', 'settings'],
};

const TAB_TO_CATEGORY: Record<SubTab, Category> = {
  webhooks: 'events',
  audit: 'events',
  broadcasts: 'events',
  trash: 'events',
  pricing: 'platform',
  fx: 'platform',
  system: 'platform',
  perf: 'platform',
  settings: 'platform',
};

export const Route = createFileRoute('/ops')({
  component: Ops,
  validateSearch: (search: Record<string, unknown>): { cat?: Category; tab?: SubTab } => {
    const tab = search.tab as SubTab | undefined;
    if (!tab) return {};
    const cat = TAB_TO_CATEGORY[tab];
    return { cat, tab };
  },
});

function Ops() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const tab: SubTab = search.tab ?? 'webhooks';
  const cat: Category = search.cat ?? TAB_TO_CATEGORY[tab];

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const setTab = (next: SubTab) => navigate({ to: '/ops', search: { cat: TAB_TO_CATEGORY[next], tab: next } });
  const setCategory = (nextCat: Category) => {
    const first = TABS_BY_CATEGORY[nextCat][0];
    navigate({ to: '/ops', search: { cat: nextCat, tab: first } });
  };

  const TAB_LABEL: Record<SubTab, string> = {
    webhooks: t('Webhooks'),
    audit: t('Audit'),
    broadcasts: t('Broadcasts'),
    trash: 'Корзина',
    pricing: 'Наценки',
    fx: t('FX'),
    system: 'System',
    perf: 'SQL',
    settings: t('Settings'),
  };

  return (
    <motion.div initial={false} className="p-4 space-y-4 pb-24">
      <h2 className="text-2xl font-bold tracking-tight px-2 pt-2">Ops</h2>

      <div className="grid grid-cols-2 gap-1.5 bg-muted/40 p-1 rounded-xl">
        <button
          type="button"
          onClick={() => setCategory('events')}
          className={`py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
            cat === 'events' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
          }`}
        >
          События
        </button>
        <button
          type="button"
          onClick={() => setCategory('platform')}
          className={`py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
            cat === 'platform' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
          }`}
        >
          Платформа
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto -mx-2 px-2">
        {TABS_BY_CATEGORY[cat].map((key) => (
          <button
            type="button"
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase whitespace-nowrap transition-colors ${
              tab === key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}
          >
            {TAB_LABEL[key]}
          </button>
        ))}
      </div>

      {tab === 'webhooks' && <WebhooksPanel />}
      {tab === 'audit' && <AuditPanel />}
      {tab === 'fx' && <FxPanel />}
      {tab === 'broadcasts' && <BroadcastsPanel />}
      {tab === 'trash' && <TrashPanel />}
      {tab === 'pricing' && <PricingPanel />}
      {tab === 'system' && (
        <>
          <AdaptgroupBalanceCard />
          <SystemSettingsPanel />
        </>
      )}
      {tab === 'perf' && <PerfPanel />}
      {tab === 'settings' && <SettingsPanel lang={i18n.language} onLang={(l) => i18n.changeLanguage(l)} />}
    </motion.div>
  );
}

function PerfPanel() {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['slow-queries'],
    queryFn: () => api.slowQueries(30),
    staleTime: 5_000,
  });

  const { mutate: reset, isPending: resetting } = useMutation({
    mutationFn: () => api.resetSlowQueries(),
    onSuccess: () => {
      haptic('success');
      toast.success('Статистика сброшена');
      qc.invalidateQueries({ queryKey: ['slow-queries'] });
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось сбросить');
    },
  });

  if (isPending) {
    return <div className="text-sm text-muted-foreground p-4">Загрузка статистики…</div>;
  }
  if (!data?.enabled) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground space-y-2">
          <div className="font-semibold text-foreground">pg_stat_statements не активен</div>
          <p>
            Расширение Postgres ещё не установлено. Нужно перезапустить postgres с
            <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-foreground">
              shared_preload_libraries=pg_stat_statements
            </code>
            и прогнать <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-foreground">alembic upgrade head</code>.
          </p>
        </CardContent>
      </Card>
    );
  }
  const items = data.items || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-2">
        <div className="text-xs text-muted-foreground">
          Топ-{items.length} по среднему времени. Сортировка mean DESC.
        </div>
        <button
          type="button"
          onClick={() => reset()}
          disabled={resetting}
          className="bg-muted text-xs font-bold uppercase px-3 py-1.5 rounded-lg active:scale-95 disabled:opacity-50"
        >
          Сбросить
        </button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Пока нет данных — сделай несколько запросов через webapp/бот.
          </CardContent>
        </Card>
      ) : (
        items.map((q, i) => <SlowQueryCard key={i} q={q} rank={i + 1} />)
      )}
    </div>
  );
}

function SlowQueryCard({
  q,
  rank,
}: {
  q: { query: string; calls: number; mean_exec_ms: number; total_exec_ms: number; max_exec_ms: number; rows: number };
  rank: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const truncated = q.query.length > 200 && !expanded;
  const display = truncated ? q.query.slice(0, 200) + '…' : q.query;
  const meanColor =
    q.mean_exec_ms > 100 ? 'text-red-500' : q.mean_exec_ms > 30 ? 'text-amber-500' : 'text-green-500';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(q.query);
      haptic('success');
      toast.success('SQL скопирован');
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
            #{rank}
          </span>
          <span className={`text-sm font-bold ${meanColor}`}>{q.mean_exec_ms.toFixed(1)} ms</span>
          <span className="text-xs text-muted-foreground">avg</span>
          <span className="ml-auto text-[11px] text-muted-foreground">
            {q.calls.toLocaleString('ru')} вызовов
          </span>
        </div>
        <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-white/[0.03] p-2 rounded-lg text-foreground/80 leading-relaxed">
          {display}
        </pre>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>Σ {(q.total_exec_ms / 1000).toFixed(1)}s</span>
          <span>·</span>
          <span>max {q.max_exec_ms.toFixed(0)}ms</span>
          <span>·</span>
          <span>~{q.rows} rows</span>
          {q.query.length > 200 && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="ml-auto text-primary font-bold"
            >
              {expanded ? 'Свернуть' : 'Развернуть'}
            </button>
          )}
          <button type="button" onClick={copy} className="text-primary font-bold">
            Copy
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function WebhooksPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: events } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => qc.getQueryData<AdminWebhookEvent[]>(['webhooks']) ?? api.webhookEvents(),
  });

  const { mutate: retry } = useMutation({
    mutationFn: (id: string) => api.retryWebhook(id).then(() => id),
    onSuccess: (id) => {
      qc.setQueryData<AdminWebhookEvent[]>(['webhooks'], (prev) =>
        (prev || []).map((e) => (e.id === id ? { ...e, status: 'ok', attempts: e.attempts + 1 } : e))
      );
      haptic('success');
      toast.success('Повторено');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось');
    },
  });

  return (
    <div className="space-y-3">
      {(events || []).map((e) => (
        <Card key={e.id}>
          <CardContent className="p-4 flex items-center gap-3">
            <WebhookStatusIcon status={e.status} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{e.event}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {e.source} · attempt {e.attempts} · {new Date(e.receivedAt).toLocaleTimeString('ru')}
              </div>
            </div>
            {e.status !== 'ok' && (
              <button
                type="button"
                onClick={() => retry(e.id)}
                className="bg-primary/15 text-primary px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 active:scale-95"
              >
                <RotateCw className="w-3.5 h-3.5" />
                {t('Webhook retry')}
              </button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function WebhookStatusIcon({ status }: { status: AdminWebhookEvent['status'] }) {
  if (status === 'ok') return <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />;
  if (status === 'failed') return <XCircle className="w-6 h-6 text-red-500 shrink-0" />;
  return <Loader2 className="w-6 h-6 text-yellow-500 animate-spin shrink-0" />;
}

function AuditPanel() {
  const { data: logs } = useQuery({ queryKey: ['audit'], queryFn: () => api.auditLog() });
  return (
    <Card>
      <CardContent className="p-0 divide-y divide-white/5">
        {(logs || []).map((l) => (
          <div key={l.id} className="p-4">
            <div className="flex items-start justify-between gap-3 mb-1">
              <span className="text-sm font-bold text-primary">{l.action}</span>
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(l.timestamp).toLocaleString('ru')}</span>
            </div>
            <div className="text-sm">{l.target}</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {l.actorName} (tg {l.actorTgId})
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function FxPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: rates } = useQuery({
    queryKey: ['fx-rates'],
    queryFn: () => qc.getQueryData<FxRate[]>(['fx-rates']) ?? api.fxRates(),
  });
  const [editing, setEditing] = useState<FxRate | null>(null);

  const { mutate: refreshCbr, isPending: refreshing } = useMutation({
    mutationFn: () => api.refreshFxFromCbr(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-rates'] });
      haptic('success');
      toast.success('Курсы обновлены из CBR');
    },
    onError: () => {
      haptic('error');
      toast.error('CBR недоступен');
    },
  });

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => refreshCbr()}
        disabled={refreshing}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]"
      >
        {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
        {t('Bulk refresh CBR')}
      </button>

      <Card>
        <CardContent className="p-0 divide-y divide-white/5">
          {(rates || []).map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => setEditing(r)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                  {r.code}
                </div>
                <div>
                  <div className="font-semibold">1 ₽ = {r.perRub.toFixed(4)} {r.code}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.source} · {new Date(r.updatedAt).toLocaleString('ru')}
                  </div>
                </div>
              </div>
              {r.manual && (
                <span className="text-[10px] font-bold text-yellow-500 bg-yellow-500/15 px-2 py-0.5 rounded-full uppercase">manual</span>
              )}
            </button>
          ))}
        </CardContent>
      </Card>

      {editing && <FxEditSheet rate={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function FxEditSheet({ rate, onClose }: { rate: FxRate; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [value, setValue] = useState<number>(rate.perRub);

  const { mutate: save, isPending } = useMutation({
    mutationFn: () => api.updateFxRate(rate.code, value),
    onSuccess: () => {
      qc.setQueryData<FxRate[]>(['fx-rates'], (prev) =>
        (prev || []).map((r) =>
          r.code === rate.code ? { ...r, perRub: value, manual: true, source: 'manual', updatedAt: new Date().toISOString() } : r
        )
      );
      haptic('success');
      toast.success(t('Rate updated'));
      onClose();
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  const valid = Number.isFinite(value) && value > 0 && value <= 10000;

  return (
    <BottomSheet open onClose={onClose} title={`Курс ${rate.code}`}>
      <p className="text-sm text-muted-foreground">Ручное переопределение перебивает автоматический CBR.</p>
      <div className="space-y-2">
        <label className="text-sm font-semibold">1 ₽ = ? {rate.code}</label>
        <input
          type="number"
          step="0.0001"
          min={0.0001}
          max={10000}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {!valid && (
          <p className="text-[11px] text-red-500">Курс должен быть положительным числом &gt; 0 и &le; 10000.</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          if (!valid) {
            toast.error('Невалидный курс');
            return;
          }
          save();
        }}
        disabled={isPending || !valid}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        {t('Save')}
      </button>
    </BottomSheet>
  );
}

/** «Каждый день · следующая 16.08, 10:00». Время — МСК, как его задают в композере. */
function platformScheduleLine(b: PlatformBroadcast): string {
  const every =
    b.recurrence === 'daily'
      ? 'Каждый день'
      : b.recurrence === 'weekly'
        ? 'Каждую неделю'
        : 'Запланирована';
  if (!b.nextFireAt) return `${every} (МСК)`;
  const next = new Date(b.nextFireAt).toLocaleString('ru', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${every} (МСК) · следующая ${next}${b.fireCount ? ` · отправлена ${b.fireCount} раз` : ''}`;
}

function BroadcastsPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: list } = useQuery({
    queryKey: ['platform-broadcasts'],
    queryFn: () => api.platformBroadcasts(),
    // Polling каждые 5с если есть running/queued — чтобы видеть прогресс отправки.
    refetchInterval: (q) => {
      const d = q.state.data;
      const active = (d || []).some((b) => b.status === 'running' || b.status === 'queued');
      return active ? 5000 : false;
    },
  });
  const [composerOpen, setComposerOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailRow = (list ?? []).find((b) => b.id === detailId) ?? null;

  const cancelMut = useMutation({
    mutationFn: (id: string) => api.cancelPlatformBroadcast(id),
    onSuccess: () => {
      toast.success('Рассылка остановлена');
      void qc.invalidateQueries({ queryKey: ['platform-broadcasts'] });
      setDetailId(null);
    },
    onError: (e: Error) => toast.error(e.message || 'Не удалось остановить'),
  });

  return (
    <>
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98]"
        >
          <Plus className="w-4 h-4" />
          Новая рассылка
        </button>

        {(list || []).length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground">Рассылок ещё не было</CardContent>
          </Card>
        ) : (
          list?.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setDetailId(b.id)}
              className="w-full text-left block active:scale-[0.99] transition-transform"
            >
              <Card>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold truncate">{b.title}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {b.audience === 'all_partners' ? t('Audience all partners') : t('Audience all clients')}
                      </div>
                    </div>
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {b.isRecurringActive ? 'повторяется' : b.status}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground line-clamp-2">{b.messagePreview ?? b.body}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {b.status === 'sent' || b.status === 'failed'
                      ? `${b.sentCount}/${b.totalCount || b.sentCount + b.failedCount} доставлено${b.failedCount ? ` · ${b.failedCount} ошибок` : ''}`
                      : b.status === 'running'
                        ? `${b.sentCount}/${b.totalCount} отправляется…`
                        : new Date(b.createdAt).toLocaleDateString('ru')}
                  </div>
                  {b.isRecurringActive && (
                    <div className="text-[11px] text-amber-500">{platformScheduleLine(b)}</div>
                  )}
                </CardContent>
              </Card>
            </button>
          ))
        )}
      </div>

      <PlatformComposer open={composerOpen} onClose={() => setComposerOpen(false)} />
      <BottomSheet open={detailId !== null} onClose={() => setDetailId(null)} title="Ход рассылки">
        {detailId && (
          <BroadcastLiveView broadcastId={detailId} fetchLive={(id) => api.platformBroadcastLive(id)} />
        )}
        {detailRow && (detailRow.isRecurringActive || detailRow.status === 'running' || detailRow.status === 'queued') && (
          <button
            type="button"
            disabled={cancelMut.isPending}
            onClick={() => {
              if (window.confirm('Остановить рассылку? Уже отправленные сообщения останутся у людей.')) {
                cancelMut.mutate(detailRow.id);
              }
            }}
            className="mt-4 w-full py-3 rounded-xl bg-red-500/15 text-red-400 text-sm font-bold disabled:opacity-50"
          >
            {cancelMut.isPending ? 'Останавливаем…' : 'Остановить рассылку'}
          </button>
        )}
      </BottomSheet>
    </>
  );
}

// Минимальный fallback на случай если бэк недоступен — те же 5 переменных
// что и в backend `AVAILABLE_VARS`. Это для статичного UI, render на бэке всё равно
// идёт по своему списку.
const PLATFORM_TEMPLATE_VARS: TemplateVar[] = [
  { name: 'username', description: 'Username клиента (или «друг»)', example: '@alice' },
  { name: 'plan_name', description: 'Текущий план клиента', example: 'Pro 30 дней' },
  { name: 'expire_date', description: 'Дата истечения (DD.MM.YYYY)', example: '25.06.2026' },
  { name: 'days_left', description: 'Дней до истечения', example: '5' },
  { name: 'bot_name', description: 'Название бота', example: 'HexVPN' },
  { name: 'bot_link', description: 'Ссылка на бота', example: 'https://t.me/hexvpnbot' },
  { name: 'date', description: 'Сегодняшняя дата (МСК)', example: '01.07.2026' },
  { name: 'time', description: 'Текущее время (МСК)', example: '14:30' },
  { name: 'weekday', description: 'День недели (МСК)', example: 'вторник' },
];

function PlatformComposer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<PlatformBroadcast['audience']>('all_partners');
  const [ratePerSecond, setRatePerSecond] = useState(25);
  const [inlineButtons, setInlineButtons] = useState<InlineButtonRows>([]);
  const [media, setMedia] = useState<MediaValue>(null);
  const { ref: bodyRef, insertAt } = useInsertAtCursor();
  const templateVars = PLATFORM_TEMPLATE_VARS;

  const { data: audienceData } = useQuery({
    queryKey: ['broadcast-audience-size', audience],
    queryFn: () => api.broadcastAudienceSize(audience),
    staleTime: 60_000,
  });
  const audienceSize = audienceData?.count ?? null;

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.createPlatformBroadcast({
        title,
        body,
        audience,
        rate_per_second: ratePerSecond,
        inline_buttons: inlineButtons.length > 0 ? inlineButtons : null,
        media_type: media?.media_type ?? null,
        media_url: media?.media_url ?? null,
      }),
    onSuccess: (created) => {
      qc.setQueryData<PlatformBroadcast[]>(['platform-broadcasts'], (prev) => [
        created,
        ...(prev || []),
      ]);
      qc.invalidateQueries({ queryKey: ['platform-broadcasts'] });
      setInlineButtons([]);
      setMedia(null);
      haptic('success');
      // Рассылка асинхронная (Taskiq), sent_count в момент create=0. Не врём «отправлено N» —
      // показываем что поставили в очередь, фактический счётчик обновится в списке через polling.
      const queued = audienceData?.count;
      toast.success(
        queued ? `Рассылка поставлена в очередь · ${queued} получателей` : 'Рассылка поставлена в очередь',
      );
      setTitle('');
      setBody('');
      onClose();
    },
    onError: () => {
      haptic('error');
      toast.error('Не отправилось');
    },
  });

  const canSend = title.trim().length > 1 && body.trim().length > 2;

  return (
    <BottomSheet open={open} onClose={onClose} title="Новая платформенная рассылка">
      <div className="space-y-4">
        {/* 1. Аудитория — наверх, потому что это первое решение которое юзер принимает */}
        <div className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            1. Кому
          </div>
          <div className="grid grid-cols-1 gap-2">
            {(['all_partners', 'all_clients_via_partners'] as PlatformBroadcast['audience'][]).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAudience(a)}
                className={`px-4 py-3 rounded-xl text-sm font-bold text-left transition-colors ${
                  audience === a ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-white/[0.04]'
                }`}
              >
                <span className="flex items-center gap-2">
                  {a === 'all_partners' ? <Handshake className="w-4 h-4 shrink-0" aria-hidden /> : <Users className="w-4 h-4 shrink-0" aria-hidden />}
                  {a === 'all_partners' ? t('Audience all partners') : t('Audience all clients')}
                </span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {audienceSize === null ? '…' : `≈ ${audienceSize} получателей`}
          </p>
        </div>

        {/* 2. Содержание */}
        <div className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            2. Сообщение
          </div>
          <MediaUploader
            value={media}
            onChange={setMedia}
            uploadFn={(file) => api.uploadPlatformMedia(file)}
          />
          <input
            placeholder="Заголовок"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={64}
            className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <textarea
            ref={bodyRef}
            placeholder="Текст рассылки (HTML работает)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={500}
            rows={5}
            className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          <TemplateVarsHelper
            vars={templateVars}
            onInsert={(token) => insertAt(token, body, setBody)}
          />
        </div>

        {/* 3. Превью — только если что-то введено */}
        {(title.trim() || body.trim()) && (
          <div className="space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              3. Превью
            </div>
            <MessagePreview title={title} body={body} vars={templateVars} />
          </div>
        )}

        {/* 4. Кнопки + скорость */}
        <ButtonsBuilder value={inlineButtons} onChange={setInlineButtons} />
        <SpeedSlider value={ratePerSecond} onChange={setRatePerSecond} total={audienceSize ?? undefined} />
      </div>

      <div className="mt-4 space-y-2">
        <TestSendButton
          disabled={!canSend}
          onSend={() =>
            api
              .testPlatformBroadcast({
                title,
                body,
                audience,
                media_type: media?.media_type ?? null,
                media_url: media?.media_url ?? null,
                inline_buttons: inlineButtons.length > 0 ? inlineButtons : null,
              })
              .then((r) => ({ sent: r.sent }))
          }
        />
        <button
          type="button"
          onClick={() => mutate()}
          disabled={!canSend || isPending}
          className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Отправить
        </button>
      </div>
    </BottomSheet>
  );
}

function SettingsPanel({ lang, onLang }: { lang: string; onLang: (l: string) => void }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0 divide-y divide-white/5">
          <button
            type="button"
            onClick={() => onLang(lang === 'en' ? 'ru' : 'en')}
            className="w-full p-4 flex justify-between items-center hover:bg-white/[0.02] transition-colors text-left"
          >
            <div className="flex gap-4 items-center">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Globe className="text-primary w-4 h-4" />
              </div>
              <div className="font-semibold">Language</div>
            </div>
            <span className="text-sm font-bold text-muted-foreground uppercase">{lang}</span>
          </button>
          <button
            type="button"
            onClick={() => getWebApp()?.openTelegramLink('https://t.me/adapt_support')}
            className="w-full p-4 flex justify-between items-center hover:bg-white/[0.02] transition-colors text-left"
          >
            <div className="flex gap-4 items-center">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <MessageCircleQuestion className="text-primary w-4 h-4" />
              </div>
              <div className="font-semibold">AdaptGroup support</div>
            </div>
            <span className="text-xs text-muted-foreground">@adapt_support</span>
          </button>
        </CardContent>
      </Card>

      <p className="text-center text-xs text-muted-foreground pt-2">v{APP_VERSION}</p>
    </div>
  );
}

function PricingPanel() {
  const qc = useQueryClient();
  const { data: markup } = useQuery({
    queryKey: ['subscription-markup'],
    queryFn: () => api.subscriptionMarkup(),
  });
  const { data: traffic } = useQuery({
    queryKey: ['traffic-markup'],
    queryFn: () => api.trafficMarkup(),
  });
  const { data: plans } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => api.plans(),
  });
  const { data: fxRates } = useQuery({
    queryKey: ['fx-rates'],
    queryFn: () => qc.getQueryData<FxRate[]>(['fx-rates']) ?? api.fxRates(),
  });
  // RUB per USD (живой курс CBR). Дефолт 100 если курс ещё не загружен.
  const perRub = fxRates?.find((r) => r.code === 'RUB')?.perRub ?? 100;

  // Локальные drafts для инпутов (% / bp).
  const [globalPct, setGlobalPct] = useState<string>('');
  const [trafficPct, setTrafficPct] = useState<string>('');

  useEffect(() => {
    if (markup) setGlobalPct(markup.global_markup_percent != null ? String(markup.global_markup_percent) : '');
  }, [markup]);
  useEffect(() => {
    if (traffic) setTrafficPct(String(traffic.markup_percent ?? 0));
  }, [traffic]);

  const { mutate: saveGlobal, isPending: savingGlobal } = useMutation({
    mutationFn: (pct: number) => api.setGlobalSubscriptionMarkup({ markup_percent: pct, markup_fixed_usd_minor: null }),
    onSuccess: (fresh) => {
      qc.setQueryData(['subscription-markup'], fresh);
      haptic('success');
      toast.success('Наценка на подписки сохранена');
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  const { mutate: saveTraffic, isPending: savingTraffic } = useMutation({
    mutationFn: (bp: number) => api.setTrafficMarkup(bp),
    onSuccess: (fresh) => {
      qc.setQueryData(['traffic-markup'], fresh);
      haptic('success');
      toast.success('Наценка на трафик сохранена');
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  const planName = (uuid: string) => plans?.find((p) => p.id === uuid)?.name ?? uuid.slice(0, 8);

  const example = markup?.example_cost_usd_minor != null && markup.example_platform_usd_minor != null;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground px-2">
        Платформенная наценка — твоя маржа поверх себестоимости AdaptGroup. Партнёр видит «с
        наценкой» как свою себестоимость и накручивает retail сверху.
      </p>

      {/* Подписки: глобальная % */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-bold">Наценка на подписки</div>
          <div className="space-y-1.5">
            <label className="text-[11px] text-muted-foreground uppercase tracking-wider font-bold">
              Глобально, %
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="decimal"
                value={globalPct}
                placeholder="например 30"
                onChange={(e) => setGlobalPct(e.target.value)}
                className="flex-1 bg-white/5 border border-white/5 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                disabled={savingGlobal || globalPct === '' || !Number.isFinite(Number(globalPct))}
                onClick={() => saveGlobal(Number(globalPct))}
                className="bg-primary text-primary-foreground px-4 py-2 rounded-xl text-xs font-bold disabled:opacity-40 flex items-center gap-1.5"
              >
                {savingGlobal && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save
              </button>
            </div>
          </div>

          {example && (
            <div className="text-[11px] text-muted-foreground bg-white/[0.02] rounded-lg px-3 py-2">
              Пример «{markup?.example_plan_name}»: себестоимость{' '}
              <span className="font-mono text-foreground">
                ${((markup!.example_cost_usd_minor ?? 0) / 100).toFixed(2)}
              </span>{' '}
              → с наценкой{' '}
              <span className="font-mono text-foreground">
                ${((markup!.example_platform_usd_minor ?? 0) / 100).toFixed(2)}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-plan overrides — только просмотр + удаление. Задаются через бэк PUT
          /pricing/subscriptions/plan/{id}; здесь не плодим инпуты на каждый план. */}
      {(markup?.per_plan?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-bold">Override по планам</div>
            <p className="text-[11px] text-muted-foreground">
              Эти планы используют свою наценку вместо глобальной.
            </p>
            <div className="divide-y divide-white/5">
              {markup!.per_plan.map((o) => (
                <PlanOverrideRow key={o.plan_uuid} name={planName(o.plan_uuid)} rule={o} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Абсолютные цены планов (₽ по живому курсу → храним USD-центы). */}
      {(markup?.plans?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-bold">Цены планов, ₽</div>
            <p className="text-[11px] text-muted-foreground">
              Задай конечную цену плана числом — она перекроет наценку. Вводишь рубли (по
              текущему курсу{' '}
              <span className="font-mono">{perRub.toFixed(2)} ₽/$</span>), храним в USD для
              margin-стабильности. Пусто — план идёт по наценке.
            </p>
            <div className="divide-y divide-white/5">
              {markup!.plans.map((p) => (
                <PlanPriceRow key={p.plan_uuid} plan={p} perRub={perRub} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Трафик */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-bold">Наценка на трафик / GB</div>
          <div className="space-y-1.5">
            <label className="text-[11px] text-muted-foreground uppercase tracking-wider font-bold">
              Глобально, %
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="decimal"
                value={trafficPct}
                placeholder="например 20"
                onChange={(e) => setTrafficPct(e.target.value)}
                className="flex-1 bg-white/5 border border-white/5 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                disabled={savingTraffic || trafficPct === '' || !Number.isFinite(Number(trafficPct))}
                onClick={() => saveTraffic(Math.round(Number(trafficPct) * 100))}
                className="bg-primary text-primary-foreground px-4 py-2 rounded-xl text-xs font-bold disabled:opacity-40 flex items-center gap-1.5"
              >
                {savingTraffic && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save
              </button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Применяется к докупке трафика (travel pack). Партнёр накручивает свою долю поверх в
            кабинете.
            {traffic?.source === 'env' && ' Сейчас значение из env (ещё не задавалось через кабинет).'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function PlanOverrideRow({ name, rule }: { name: string; rule: { plan_uuid: string; markup_percent: number | null; markup_fixed_usd_minor: number | null } }) {
  const qc = useQueryClient();
  const { mutate: remove, isPending } = useMutation({
    mutationFn: () => api.deletePlanSubscriptionMarkup(rule.plan_uuid),
    onSuccess: (fresh) => {
      qc.setQueryData(['subscription-markup'], fresh);
      haptic('success');
      toast.success('Override снят');
    },
    onError: () => {
      haptic('error');
      toast.error('Не получилось');
    },
  });
  const label =
    rule.markup_fixed_usd_minor != null
      ? `+$${(rule.markup_fixed_usd_minor / 100).toFixed(2)}`
      : `+${rule.markup_percent}%`;
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate">{name}</div>
        <div className="text-[11px] text-muted-foreground font-mono">{label}</div>
      </div>
      <button
        type="button"
        disabled={isPending}
        onClick={() => remove()}
        className="bg-muted text-xs font-bold uppercase px-3 py-1.5 rounded-lg active:scale-95 disabled:opacity-50"
      >
        Снять
      </button>
    </div>
  );
}

function PlanPriceRow({ plan, perRub }: { plan: PlanPriceInfo; perRub: number }) {
  const qc = useQueryClient();
  // Текущая цена в ₽: если задана абсолютная — её, иначе себестоимость (для ориентира).
  const baseMinor = plan.fixed_price_usd_minor ?? plan.cost_usd_minor;
  const currentRub = (baseMinor / 100) * perRub;
  const costRub = (plan.cost_usd_minor / 100) * perRub;

  const [draft, setDraft] = useState<string>('');
  useEffect(() => {
    setDraft(
      plan.fixed_price_usd_minor != null
        ? String(Math.round((plan.fixed_price_usd_minor / 100) * perRub))
        : '',
    );
  }, [plan.fixed_price_usd_minor, perRub]);

  const { mutate: savePrice, isPending: saving } = useMutation({
    mutationFn: (rub: number) => {
      // ₽ → USD-центы по живому курсу: usd_minor = round(rub / perRub * 100).
      const usdMinor = Math.round((rub / perRub) * 100);
      return api.setPlanSubscriptionPrice(plan.plan_uuid, usdMinor);
    },
    onSuccess: (fresh) => {
      qc.setQueryData(['subscription-markup'], fresh);
      haptic('success');
      toast.success('Цена плана сохранена');
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  const { mutate: resetPrice, isPending: resetting } = useMutation({
    mutationFn: () => api.deletePlanSubscriptionPrice(plan.plan_uuid),
    onSuccess: (fresh) => {
      qc.setQueryData(['subscription-markup'], fresh);
      haptic('success');
      toast.success('Сброшено к наценке');
    },
    onError: () => {
      haptic('error');
      toast.error('Не получилось');
    },
  });

  const draftNum = Number(draft);
  const draftValid = draft !== '' && Number.isFinite(draftNum) && draftNum > 0;

  return (
    <div className="py-2.5 space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">
            {plan.plan_name}{' '}
            <span className="text-[11px] text-muted-foreground font-normal">
              {plan.devices}× · {plan.days}д
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground font-mono">
            себестоимость ≈ {costRub.toFixed(0)} ₽
            {plan.fixed_price_usd_minor == null && ' · цена по наценке'}
            {plan.fixed_price_usd_minor != null && ` · сейчас ${currentRub.toFixed(0)} ₽`}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          type="number"
          inputMode="numeric"
          value={draft}
          placeholder="цена ₽"
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 bg-white/5 border border-white/5 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          type="button"
          disabled={saving || !draftValid}
          onClick={() => savePrice(draftNum)}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-xl text-xs font-bold disabled:opacity-40 flex items-center gap-1.5"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Save
        </button>
        {plan.fixed_price_usd_minor != null && (
          <button
            type="button"
            disabled={resetting}
            onClick={() => resetPrice()}
            className="bg-muted text-xs font-bold px-3 py-2 rounded-xl active:scale-95 disabled:opacity-50"
          >
            Сброс
          </button>
        )}
      </div>
    </div>
  );
}

function AdaptgroupBalanceCard() {
  const { data, isPending } = useQuery({
    queryKey: ['adaptgroup-balance'],
    queryFn: () => api.adaptgroupBalance(),
    refetchInterval: 5 * 60 * 1000,
  });
  const low = data?.balance !== null && data !== undefined && Number(data.balance) < data.threshold_usd;
  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[12px] text-muted-foreground">Баланс AdaptGroup</div>
            {isPending ? (
              <div className="mt-1 h-6 w-24 animate-pulse rounded bg-white/10" />
            ) : (
              <div className={low ? 'mt-1 font-mono text-lg font-bold text-red-500' : 'mt-1 font-mono text-lg font-bold'}>
                {data?.balance !== null && data ? `$${Number(data.balance).toFixed(2)}` : 'нет данных'}
              </div>
            )}
          </div>
          <div className="text-right text-[11px] text-muted-foreground">
            <div>порог ${data?.threshold_usd ?? '…'}</div>
            {data?.checked_at ? <div>проверено {new Date(data.checked_at).toLocaleTimeString('ru')}</div> : null}
          </div>
        </div>
        {low ? (
          <div className="mt-2 text-[12px] text-red-500">
            Ниже порога: при нуле оплаты проходят, а подписки не выдаются. Пополните.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}


function SystemSettingsPanel() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => api.systemSettings(),
  });
  const [edits, setEdits] = useState<Record<string, string>>({});

  const { mutate: save, isPending } = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => api.updateSystemSetting(key, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['system-settings'] });
      haptic('success');
      toast.success('Сохранено');
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  if (!settings) return null;

  const handleSave = (key: string, raw: string) => {
    // Пытаемся распарсить как JSON (числа, bool, объекты). Иначе пишем как строку.
    let parsed: unknown = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // оставляем как строку
    }
    save({ key, value: parsed });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground px-2">
        Глобальные дефолты для всей платформы. Меняются без рестарта. JSON-литералы (числа, true/false)
        и строки.
      </p>
      {settings.map((s) => {
        const current = edits[s.key] ?? JSON.stringify(s.value);
        const dirty = current !== JSON.stringify(s.value);
        return (
          <Card key={s.key}>
            <CardContent className="p-4 space-y-2">
              <div className="text-sm font-bold font-mono">{s.key}</div>
              {s.description && (
                <p className="text-[11px] text-muted-foreground">{s.description}</p>
              )}
              <div className="flex gap-2">
                <input
                  value={current}
                  onChange={(e) => setEdits((p) => ({ ...p, [s.key]: e.target.value }))}
                  className="flex-1 bg-white/5 border border-white/5 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  type="button"
                  disabled={!dirty || isPending}
                  onClick={() => handleSave(s.key, current)}
                  className="bg-primary text-primary-foreground px-3 py-2 rounded-xl text-xs font-bold disabled:opacity-40"
                >
                  Save
                </button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function TrashPanel() {
  const qc = useQueryClient();
  const { data: partners } = useQuery({
    queryKey: ['trashed-partners'],
    queryFn: () => api.trashedPartners(),
  });
  const { data: bots } = useQuery({
    queryKey: ['trashed-bots'],
    queryFn: () => api.trashedBots(),
  });
  const { data: plans } = useQuery({
    queryKey: ['trashed-plans'],
    queryFn: () => api.trashedPlans(),
  });

  const { mutate: restoreBot } = useMutation({
    mutationFn: (id: string) => api.restoreBot(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trashed-bots'] });
      haptic('success');
      toast.success('Бот восстановлен');
    },
    onError: (e) => {
      haptic('error');
      toast.error(apiErrorMessage(e) ?? 'Не получилось');
    },
  });

  const { mutate: unbanPartner } = useMutation({
    mutationFn: (id: string) =>
      api.banPartner(id, false).then(() => id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trashed-partners'] });
      haptic('success');
      toast.success('Партнёр разблокирован');
    },
    onError: () => {
      haptic('error');
      toast.error('Не получилось');
    },
  });

  return (
    <div className="space-y-4">
      <Section title={`Партнёры (${partners?.length ?? 0})`}>
        {(partners || []).map((p) => (
          <TrashRow
            key={p.id}
            title={p.display_name}
            subtitle={`tg:${p.tg_id ?? '—'} · ${new Date(p.banned_at).toLocaleDateString('ru')}`}
            onRestore={async () => {
              if (!(await confirmAction(`Разблокировать партнёра ${p.display_name}?`))) return;
              unbanPartner(p.id);
            }}
          />
        ))}
        {partners?.length === 0 && <Empty />}
      </Section>

      <Section title={`Боты (${bots?.length ?? 0})`}>
        {(bots || []).map((b) => (
          <TrashRow
            key={b.id}
            title={b.title}
            subtitle={`@${b.username} · ${b.status} · ${new Date(b.deleted_at).toLocaleDateString('ru')}`}
            onRestore={async () => {
              if (!(await confirmAction(`Восстановить бота @${b.username}? Если партнёр уже удалил его в BotFather — восстановление не сработает.`))) return;
              restoreBot(b.id);
            }}
          />
        ))}
        {bots?.length === 0 && <Empty />}
      </Section>

      <Section title={`Планы (${plans?.length ?? 0})`}>
        {(plans || []).map((p) => (
          <div key={p.id} className="p-4 border-t border-white/5">
            <div className="font-semibold">{p.name}</div>
            <div className="text-[11px] text-muted-foreground">
              {p.days}d · {p.devices} устр · скрыт {new Date(p.hidden_at).toLocaleDateString('ru')}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Восстановить — в Plans, переключить visibility
            </div>
          </div>
        ))}
        {plans?.length === 0 && <Empty />}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-2">{title}</h3>
      <Card>
        <CardContent className="p-0 divide-y divide-white/5">{children}</CardContent>
      </Card>
    </div>
  );
}

function TrashRow({
  title,
  subtitle,
  onRestore,
}: {
  title: string;
  subtitle: string;
  onRestore: () => void;
}) {
  return (
    <div className="p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-semibold truncate">{title}</div>
        <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>
      </div>
      <button
        type="button"
        onClick={onRestore}
        className="bg-primary/15 text-primary px-3 py-2 rounded-xl text-xs font-bold active:scale-95"
      >
        Восстановить
      </button>
    </div>
  );
}

function Empty() {
  return <div className="p-6 text-sm text-muted-foreground text-center">Пусто</div>;
}
